// Pure data helpers for the Company Stock advice step. The step reflects the
// recommendation to sell unrestricted company stock (we always recommend 100%)
// and any modification the user made in the "make changes" drawer. It reads the
// active engine case's company-stock holdings (see src/models/CompanyStock.js
// and the unrestrictedCompanyStocks / restrictedCompanyStocks getters in
// src/models/Engine.js) — nothing here fetches or mutates.

import { numberToDollars } from '../../../../utils'

/** "$1,234" from a raw dollar number. */
const dollars = value => `$${numberToDollars(value || 0, true)}`

const sumBy = (holdings, key) => holdings.reduce((total, stock) => total + (stock[key] || 0), 0)

/**
 * Whether the Company Stock step is reachable: the feature is on and the active
 * case has at least one company-stock holding. Advice uses this to skip the step
 * during traversal and to guard its route (mirrors hasIncludedGoals /
 * hasInvestmentAccounts).
 */
export function hasCompanyStock({ features, activeCase } = {}) {
  return !!features?.companyStockEnabled && (activeCase?.companyStocks?.length ?? 0) > 0
}

/**
 * Aggregate, dollar-weighted percentage of unrestricted company stock being
 * sold — the "XX%" shown in the modified heading. Dollar-weighting keeps it
 * correct when there are multiple unrestricted holdings (unlike summing each
 * holding's percentToSell).
 */
export function unrestrictedSellPercent(unrestricted = []) {
  const totalValue = sumBy(unrestricted, 'totalValue')
  if (totalValue <= 0) {
    return 0
  }
  return Math.round((sumBy(unrestricted, 'amountToSell') / totalValue) * 100)
}

/**
 * Table rows for the unrestricted section. Current = the holding's current
 * value; recommended = value remaining after the sale; the change sentence
 * describes the sale (or that the shares are being kept when nothing is sold).
 */
export function buildUnrestrictedRows(unrestricted = []) {
  return unrestricted.map(stock => {
    const sold = stock.amountToSell || 0
    return {
      id: stock.positionId,
      name: stock.securityName,
      label: 'UNRESTRICTED',
      current: dollars(stock.totalValue),
      recommended: dollars(stock.totalValue - sold),
      changeText:
        sold > 0
          ? `Sell ${dollars(sold)} worth of unrestricted shares.`
          : "You've chosen to keep your unrestricted shares.",
    }
  })
}

/**
 * Table rows for the restricted section. These can't be sold, so we give no
 * advice on them — current shows the value, the recommended column is a literal
 * "Unable to sell", and there is no change sentence.
 */
export function buildRestrictedRows(restricted = []) {
  return restricted.map(stock => ({
    id: stock.positionId,
    name: stock.securityName,
    label: 'RESTRICTED',
    current: dollars(stock.totalValue),
    recommended: 'Unable to sell',
    changeText: null,
  }))
}

/** Account nickname + total balance for the lock-up accordion. */
export function buildBalance({ accountName, balance } = {}) {
  return {
    accountName: accountName || 'your account',
    totalBalance: dollars(balance),
  }
}

/**
 * Current vs recommended company-stock concentration for the two pie charts:
 * (restricted + unrestricted) as a percentage of, and share of, the total
 * account balance. Restricted stock is always held, so it appears in both
 * columns; only the unrestricted amount being sold reduces the recommended
 * figure.
 */
export function buildPie({ unrestricted = [], restricted = [], balance = 0 } = {}) {
  const denominator = balance || 1
  const currentValue = sumBy(unrestricted, 'totalValue') + sumBy(restricted, 'totalValue')
  const recommendedValue = currentValue - sumBy(unrestricted, 'amountToSell')

  const slice = value => ({
    percent: (value / denominator) * 100,
    dollar: dollars(value),
  })

  return { current: slice(currentValue), recommended: slice(recommendedValue) }
}
